import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Turnstile from "@/components/Turnstile";
import { useSeo } from "@/lib/useSeo";

interface DateSlot { date: string; time: string }
const emptySlot = (): DateSlot => ({ date: "", time: "" });

export default function PublicForm() {
  useSeo({
    title: "Henry Miller Museum · Plan a group visit",
    description:
      "Request a group visit to the Henry Miller Museum in St. Louis, the boarding house where the IBEW was founded in 1891. Schools, locals, and historical societies welcome by appointment.",
  });
  const navigate = useNavigate();
  const siteKey = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) ?? "";

  const [form, setForm] = useState({
    organization: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    address: "",
    attendees_count: "",
    attendees_adults: "",
    attendees_children: "",
    age_ranges: "",
    tour_type: "",
    accessibility: "",
    purpose: "",
    photo_release: null as boolean | null,
    heard_from: "",
    ibew_affiliation: "",
    volunteer_interest: false,
    website: "",
  });
  const [requested, setRequested] = useState<DateSlot[]>([emptySlot()]);
  const [alternates, setAlternates] = useState<DateSlot[]>([]);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function set<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }
  function setSlot(list: DateSlot[], setList: (v: DateSlot[]) => void, i: number, patch: Partial<DateSlot>) {
    setList(list.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        attendees_count: Number(form.attendees_count) || 0,
        attendees_adults: form.attendees_adults === "" ? undefined : Number(form.attendees_adults),
        attendees_children: form.attendees_children === "" ? undefined : Number(form.attendees_children),
        requested_dates: requested.filter((s) => s.date),
        alternate_dates: alternates.filter((s) => s.date),
        turnstile_token: turnstileToken || undefined,
      };
      const r = await api.submitVisit(payload);
      navigate(`/thank-you?id=${r.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      {/* ─────────── HERO ─────────── */}
      <section className="border-b border-museum-line bg-museum-paper">
        <div className="max-w-6xl mx-auto px-5 pt-12 pb-10 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-eyebrow text-museum-accent2 font-semibold mb-3">
              Group visits · By appointment
            </div>
            <h1 className="font-serif text-[40px] sm:text-[52px] leading-[1.05] text-museum-ink">
              Visit the birthplace of the{" "}
              <em className="not-italic text-museum-accent">IBEW</em>.
            </h1>
            <div className="rule-gold mt-5" />
            <p className="mt-4 text-museum-muted max-w-prose leading-relaxed">
              At 2728 Martin Luther King Drive in St. Louis, the Henry Miller Museum
              occupies the boarding house where the International Brotherhood of
              Electrical Workers was founded in 1891. The museum is run by IBEW
              member volunteers and welcomes schools, locals, historical societies,
              and other organizations by appointment. Tell us about your group
              below, and a volunteer will follow up to arrange a visit.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="#request" className="btn-primary">Begin request</a>
              <a
                href="https://ibew.org/our-history-museum/henry-miller-museum/"
                target="_blank" rel="noopener noreferrer"
                className="btn-secondary"
              >
                About the museum
              </a>
            </div>
          </div>
          <div className="relative">
            <div className="absolute inset-0 -m-2 border border-museum-rule/60 rounded-sm pointer-events-none" />
            <picture>
              <source
                type="image/webp"
                srcSet="/hero-sm.webp 1200w, /hero.webp 2000w"
                sizes="(min-width: 768px) 50vw, 100vw"
              />
              <img
                src="/hero-sm.jpg"
                srcSet="/hero-sm.jpg 1200w, /hero.jpg 2000w"
                sizes="(min-width: 768px) 50vw, 100vw"
                width={2000}
                height={1085}
                alt="The Henry Miller Museum, a two-story red-brick building in St. Louis with 'Henry Miller Museum' signage above the storefront windows."
                className="block w-full rounded-sm bg-museum-paperDeep object-cover aspect-[1.85/1]"
                loading="eager"
                decoding="async"
              />
            </picture>
          </div>
        </div>
      </section>

      {/* ─────────── BODY ─────────── */}
      <section id="request" className="max-w-6xl mx-auto px-5 py-12 grid lg:grid-cols-[2fr_1fr] gap-8">
        {/* ─── Form ─── */}
        <form className="card p-7 sm:p-9 space-y-7" onSubmit={onSubmit} noValidate>
          <header>
            <div className="section-eyebrow">Visit request</div>
            <h2 className="section-title">Tell us about your group</h2>
            <div className="rule-gold" />
            <p className="text-sm text-museum-muted">
              All fields required unless noted. We'll respond within five business days.
            </p>
          </header>

          {/* honeypot */}
          <input type="text" name="website" tabIndex={-1} autoComplete="off"
                 value={form.website} onChange={(e) => set("website", e.target.value)}
                 className="hidden" aria-hidden />

          <div className="form-group">
            <h3 className="form-group-title">Contact</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="label" htmlFor="organization">
                  Organization / group                </label>
                <input id="organization" required className="input" value={form.organization}
                       onChange={(e) => set("organization", e.target.value)} />
              </div>
              <div>
                <label className="label" htmlFor="contact_name">Contact name </label>
                <input id="contact_name" required className="input" value={form.contact_name}
                       onChange={(e) => set("contact_name", e.target.value)} />
              </div>
              <div>
                <label className="label" htmlFor="contact_email">Contact email </label>
                <input id="contact_email" type="email" required className="input"
                       value={form.contact_email}
                       onChange={(e) => set("contact_email", e.target.value)} />
              </div>
              <div>
                <label className="label" htmlFor="contact_phone">Contact phone </label>
                <input id="contact_phone" type="tel" required inputMode="tel"
                       autoComplete="tel" className="input"
                       value={form.contact_phone}
                       onChange={(e) => set("contact_phone", e.target.value)} />
              </div>
              <div className="sm:col-span-2">
                <label className="label" htmlFor="address">
                  Mailing address                </label>
                <input id="address" required autoComplete="street-address"
                       className="input" value={form.address}
                       onChange={(e) => set("address", e.target.value)} />
              </div>
            </div>
          </div>

          <div className="form-group">
            <h3 className="form-group-title">Attendees</h3>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <label className="label" htmlFor="attendees_count">Total </label>
                <input id="attendees_count" type="number" min={1} required className="input"
                       value={form.attendees_count}
                       onChange={(e) => set("attendees_count", e.target.value)} />
              </div>
              <div>
                <label className="label" htmlFor="attendees_adults">Adults <span className="text-museum-muted/80 normal-case font-normal tracking-normal">(optional)</span></label>
                <input id="attendees_adults" type="number" min={0} className="input"
                       value={form.attendees_adults}
                       onChange={(e) => set("attendees_adults", e.target.value)} />
              </div>
              <div>
                <label className="label" htmlFor="attendees_children">Children <span className="text-museum-muted/80 normal-case font-normal tracking-normal">(optional)</span></label>
                <input id="attendees_children" type="number" min={0} className="input"
                       value={form.attendees_children}
                       onChange={(e) => set("attendees_children", e.target.value)} />
              </div>
              <div className="sm:col-span-3">
                <label className="label" htmlFor="age_ranges">Age ranges <span className="text-museum-muted/80 normal-case font-normal tracking-normal">(if a school group)</span></label>
                <input id="age_ranges" className="input" placeholder="e.g. Grades 5 to 8"
                       value={form.age_ranges}
                       onChange={(e) => set("age_ranges", e.target.value)} />
              </div>
            </div>
          </div>

          <div className="form-group">
            <h3 className="form-group-title">Preferred dates</h3>
            <p className="help">Up to three preferred date and time combinations.</p>
            <p className="text-sm text-museum-ink mt-1">
              <em className="font-serif not-italic text-museum-accent2">Please note —</em>{" "}
              weekend tours are unavailable. Please choose a weekday.
            </p>
            {requested.map((s, i) => (
              <div key={`req-${i}`} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                <div>
                  <label className="label">Date {i + 1}{i > 0 && <span className="text-museum-muted/80 normal-case font-normal tracking-normal"> (optional)</span>}</label>
                  <input type="date" required={i === 0} className="input" value={s.date}
                         onChange={(e) => setSlot(requested, setRequested, i, { date: e.target.value })} />
                </div>
                <div>
                  <label className="label">Time</label>
                  <input type="time" className="input" value={s.time}
                         onChange={(e) => setSlot(requested, setRequested, i, { time: e.target.value })} />
                </div>
                <div className="pb-1">
                  {requested.length > 1 && (
                    <button type="button" className="btn-ghost"
                            onClick={() => setRequested(requested.filter((_, idx) => idx !== i))}>
                      Remove
                    </button>
                  )}
                </div>
              </div>
            ))}
            {requested.length < 3 && (
              <button type="button" className="btn-secondary"
                      onClick={() => setRequested([...requested, emptySlot()])}>
                + Add another preferred date
              </button>
            )}

            <div className="pt-3">
              <h4 className="font-serif text-base text-museum-ink">Alternate dates</h4>
              <p className="help mb-2">Optional fallback dates if our calendar doesn't fit your top choices.</p>
              {alternates.map((s, i) => (
                <div key={`alt-${i}`} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end mt-2">
                  <div>
                    <label className="label">Alt {i + 1}</label>
                    <input type="date" className="input" value={s.date}
                           onChange={(e) => setSlot(alternates, setAlternates, i, { date: e.target.value })} />
                  </div>
                  <div>
                    <label className="label">Time</label>
                    <input type="time" className="input" value={s.time}
                           onChange={(e) => setSlot(alternates, setAlternates, i, { time: e.target.value })} />
                  </div>
                  <div className="pb-1">
                    <button type="button" className="btn-ghost"
                            onClick={() => setAlternates(alternates.filter((_, idx) => idx !== i))}>
                      Remove
                    </button>
                  </div>
                </div>
              ))}
              {alternates.length < 3 && (
                <button type="button" className="btn-secondary mt-3"
                        onClick={() => setAlternates([...alternates, emptySlot()])}>
                  + Add an alternate
                </button>
              )}
            </div>
          </div>

          <div className="form-group">
            <h3 className="form-group-title">Visit details</h3>
            <div>
              <label className="label" htmlFor="tour_type">
                Tour or program              </label>
              <select id="tour_type" required className="select" value={form.tour_type}
                      onChange={(e) => set("tour_type", e.target.value)}>
                <option value="">Select one</option>
                <option>Standard guided tour</option>
                <option>Educational / school program</option>
                <option>IBEW history focus</option>
                <option>Other (describe below)</option>
              </select>
            </div>
            <div>
              <label className="label" htmlFor="purpose">
                Purpose / brief description              </label>
              <textarea id="purpose" required className="textarea" value={form.purpose}
                        onChange={(e) => set("purpose", e.target.value)} />
            </div>
            <div>
              <label className="label" htmlFor="accessibility">
                Accessibility needs or special accommodations              </label>
              <textarea
                id="accessibility"
                required
                className="textarea"
                placeholder="Write 'None' if no accommodations are needed."
                value={form.accessibility}
                onChange={(e) => set("accessibility", e.target.value)}
              />
            </div>
            <fieldset className="p-4 rounded-sm bg-museum-paperDeep/60 border border-museum-line">
              <legend className="px-1 text-[11px] uppercase tracking-eyebrow font-semibold text-museum-muted">
                Social media photos              </legend>
              <p className="text-sm text-museum-ink mb-3">
                Are you OK with images of your event being shared on the
                museum's social media pages?
              </p>
              <div className="grid sm:grid-cols-2 gap-2">
                <label className={"flex items-start gap-3 p-3 rounded-sm border cursor-pointer transition " +
                  (form.photo_release === true
                    ? "border-museum-accent bg-museum-paper"
                    : "border-museum-line bg-museum-paper hover:bg-museum-paperDeep")}>
                  <input
                    type="radio"
                    name="photo_release"
                    required
                    className="mt-1 accent-museum-accent"
                    checked={form.photo_release === true}
                    onChange={() => set("photo_release", true)}
                  />
                  <span className="text-sm leading-snug">
                    <strong className="block">Yes, sharing is fine.</strong>
                    <span className="text-museum-muted">
                      Photos of our group may be posted.
                    </span>
                  </span>
                </label>
                <label className={"flex items-start gap-3 p-3 rounded-sm border cursor-pointer transition " +
                  (form.photo_release === false
                    ? "border-museum-accent bg-museum-paper"
                    : "border-museum-line bg-museum-paper hover:bg-museum-paperDeep")}>
                  <input
                    type="radio"
                    name="photo_release"
                    required
                    className="mt-1 accent-museum-accent"
                    checked={form.photo_release === false}
                    onChange={() => set("photo_release", false)}
                  />
                  <span className="text-sm leading-snug">
                    <strong className="block">No, please don't share images of our group.</strong>
                    <span className="text-museum-muted">
                      Staff will be informed.
                    </span>
                  </span>
                </label>
              </div>
            </fieldset>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="label" htmlFor="heard_from">
                  How did you hear about us?                </label>
                <input id="heard_from" required className="input" value={form.heard_from}
                       onChange={(e) => set("heard_from", e.target.value)} />
              </div>
              <div>
                <label className="label" htmlFor="ibew_affiliation">
                  IBEW affiliation                </label>
                <input id="ibew_affiliation" required className="input"
                       placeholder="Local number, role, or 'None' if not affiliated"
                       value={form.ibew_affiliation}
                       onChange={(e) => set("ibew_affiliation", e.target.value)} />
              </div>
            </div>
          </div>

          <div className="form-group">
            <h3 className="form-group-title">Volunteering</h3>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 accent-museum-accent"
                checked={form.volunteer_interest}
                onChange={(e) => set("volunteer_interest", e.target.checked)}
              />
              <span className="text-sm leading-snug text-museum-ink">
                <strong className="block">
                  I'm an IBEW member and would like to become a volunteer.
                </strong>
                <span className="text-museum-muted">
                  I live in the region and can help open the museum for visits.
                  Staff will follow up about volunteer opportunities.
                </span>
              </span>
            </label>
          </div>

          {siteKey && <Turnstile siteKey={siteKey} onToken={setTurnstileToken} />}

          {error && (
            <div className="text-sm bg-red-50 border border-museum-accent/30 text-museum-accent rounded-sm p-3">
              {error}
            </div>
          )}

          <div className="flex items-center justify-between gap-4 pt-2">
            <p className="text-xs text-museum-muted">
              We'll only use your information to coordinate this visit.
            </p>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? "Submitting…" : "Send request →"}
            </button>
          </div>
        </form>

        {/* ─── Sidebar ─── */}
        <aside className="space-y-5">
          <div className="card-warm p-6">
            <div className="section-eyebrow">What to expect</div>
            <h2 className="font-serif text-xl mb-3">A few notes for your group</h2>
            <ul className="text-sm text-museum-ink/90 space-y-2.5 leading-relaxed">
              <li className="flex gap-2">
                <span className="text-museum-accent2">·</span>
                Tours typically run 60 to 90 minutes.
              </li>
              <li className="flex gap-2">
                <span className="text-museum-accent2">·</span>
                The museum is a 19th-century home. Please note any
                accessibility needs in the form.
              </li>
              <li className="flex gap-2">
                <span className="text-museum-accent2">·</span>
                <span>
                  The museum is volunteer-run, so weekday availability
                  depends on volunteers and how much advance notice we
                  get. <strong>Weekend tours are unavailable</strong> —
                  please choose a weekday. We'll do our best to confirm
                  a date that works, or suggest alternatives.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-museum-accent2">·</span>
                We'll email you a confirmation once your date is approved.
              </li>
            </ul>
          </div>

          <div className="card overflow-hidden">
            <picture>
              <source type="image/webp" srcSet="/lineman.webp" />
              <img
                src="/lineman.jpg"
                width={1400}
                height={788}
                alt="Bronze sculpture of a lineman against the museum's brick interior wall."
                className="block w-full aspect-[1.78/1] object-cover"
                loading="lazy"
                decoding="async"
              />
            </picture>
            <div className="p-6">
              <div className="section-eyebrow">About the museum</div>
              <h2 className="font-serif text-xl mb-3">Where the IBEW was founded</h2>
              <p className="text-sm text-museum-muted leading-relaxed">
                Henry Miller, a traveling lineman, organized the 1891 founding
                convention of the National Brotherhood of Electrical Workers in
                this very building. Carefully restored beginning in 2015 and
                reopened to the public in 2016, the museum is operated by the
                Electrical Workers Historical Society, a 501(c)(3) nonprofit
                affiliated with the IBEW, and staffed by{" "}
                <span className="text-museum-ink">IBEW member volunteers</span>.
              </p>
              <a
                href="https://ibew.org/our-history-museum/henry-miller-museum/"
                target="_blank" rel="noopener noreferrer"
                className="inline-block text-sm text-museum-accent mt-3 border-b border-museum-rule/60 pb-0.5"
              >
                Read more on ibew.org →
              </a>
            </div>
          </div>

          <div className="text-xs text-museum-muted px-1">
            Questions? Email{" "}
            <a className="text-museum-accent" href="mailto:members@ibewlocal1.org">
              members@ibewlocal1.org
            </a>
            .
          </div>
        </aside>
      </section>

      {/* ─────────── HOW THE MUSEUM IS RUN ─────────── */}
      <section className="border-t border-museum-line bg-museum-paperDeep/40">
        <div className="max-w-4xl mx-auto px-5 py-14 sm:py-16">
          <div className="section-eyebrow">About the museum</div>
          <h2 className="font-serif text-[30px] sm:text-[38px] leading-tight text-museum-ink">
            How the museum is run
          </h2>
          <div className="rule-gold mt-5" />
          <div className="mt-6 text-museum-ink/90 leading-relaxed space-y-5">
            <p>
              The Henry Miller Museum is operated by the Electrical Workers
              Historical Society, a 501(c)(3) nonprofit established in 2015 to
              restore and maintain the boarding house where the IBEW was
              founded in 1891. The property was originally purchased by Local 1's
              building corporation and transferred to the Society for stewardship.
            </p>
            <p>
              The Society is governed by a board of directors drawn from the
              leadership of the union: the IBEW International President, the
              International Secretary-Treasurer, the 11th District International
              Vice President, the Business Manager of Local 1 (IBEW), and one
              at-large director representing Local 1.
            </p>
            <p>
              The Society's mission, in its own words, is{" "}
              <em className="not-italic text-museum-ink">
                "to educate and enlighten the work of the Brotherhood and the
                broader labor movement."
              </em>{" "}
              Funding comes from donations by IBEW members and local unions
              across North America, and every dollar raised goes to restoring,
              maintaining, and operating the museum.
            </p>
            <p>
              The home stands today as a working testament to the early
              American trade union movement, and to the ten linemen who, in
              1891, organized for fair pay and safer conditions in what was
              then a brand new and often dangerous trade.
            </p>
          </div>
          <p className="mt-8 text-sm text-museum-muted">
            To support the Society's work, see{" "}
            <a
              className="text-museum-accent border-b border-museum-rule/60 pb-0.5"
              href="https://ibew.org/our-history-museum/henry-miller-museum/"
              target="_blank"
              rel="noopener noreferrer"
            >
              ibew.org/our-history-museum/henry-miller-museum
            </a>
            .
          </p>
        </div>
      </section>
    </>
  );
}
